// ── Adapter Supabase do ITagRepository ────────────────────────────
// Wrappers em torno das tabelas public.tags e public.lead_tags.

import { getSupabase } from '@infra/supabase/client';
import type {
  ITagRepository,
  Tag,
  LeadTag,
  InsertTagParams,
  TagUpdates,
} from '@modules/config';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseTagRepository implements ITagRepository {
  listTags = async (workspaceId: string): Promise<Tag[]> => {
    const { data, error } = await client()
      .from('tags')
      .select('id, name, color, ativo, workspace_id')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true)
      .order('name');
    if (error) throw error;
    return (data || []) as unknown as Tag[];
  };

  insertTag = async ({ workspaceId, name, color }: InsertTagParams): Promise<Tag> => {
    const { data, error } = await client()
      .from('tags')
      .insert({
        workspace_id: workspaceId,
        name,
        color,
        ativo: true,
      })
      .select('id, name, color, ativo, workspace_id')
      .single();
    if (error) throw error;
    return data as unknown as Tag;
  };

  updateTag = async (tagId: string, updates: TagUpdates): Promise<Tag> => {
    const { data, error } = await client()
      .from('tags')
      .update(updates)
      .eq('id', tagId)
      .select('id, name, color, ativo, workspace_id')
      .single();
    if (error) throw error;
    return data as unknown as Tag;
  };

  insertLeadTag = async (leadId: string, tagId: string): Promise<LeadTag | null> => {
    const { data, error } = await client()
      .from('lead_tags')
      .insert({ lead_id: leadId, tag_id: tagId })
      .select('id, lead_id, tag_id')
      .single();

    // Ignora erro de duplicata (constraint UNIQUE em lead_id, tag_id)
    if (error) {
      if (error.code === '23505') return null;
      throw error;
    }
    return data as unknown as LeadTag;
  };

  deleteLeadTag = async (leadId: string, tagId: string): Promise<void> => {
    const { error } = await client()
      .from('lead_tags')
      .delete()
      .eq('lead_id', leadId)
      .eq('tag_id', tagId);
    if (error) throw error;
  };

  listTagsByLead = async (leadId: string): Promise<Tag[]> => {
    const { data, error } = await client()
      .from('lead_tags')
      .select('tag_id, tags ( id, name, color, ativo, workspace_id )')
      .eq('lead_id', leadId);
    if (error) throw error;
    return ((data || []) as unknown as Array<{ tags: Tag | null }>)
      .map(row => row.tags)
      .filter((t): t is Tag => !!t && t.ativo !== false);
  };
}
